import React, { useState, forwardRef } from 'react'
import { Card as AntdCard } from 'antd'
import type { CardProps as Props } from 'antd/es/card'
import cx from 'classnames'
import { createKeydownFromClick } from 'keydown-from-click'
import { isBoolean } from 'lodash'
import { Checkbox } from '../Checkbox'
import './styles.scss'

const { Grid, Meta } = AntdCard
const cardClsPrefix = 'app-card'

export interface CardProps extends Props {
  // Overwrite Card component loading state
  loadingContent?: React.ReactNode
  /**
   * Will display a border on hover and when selected with a checkmark
   * at the bottom right corner of the Card
   */
  selectable?: boolean
  /**
   * Controlled value to indicate if card is selected
   */
  isSelected?: boolean
  initialIsSelected?: boolean
  /**
   * Component to replace checkbox component when Card is 'selectable'
   */
  checkbox?: React.ReactNode
  /**
   * See PlanCard component to migrate that styling to this component.
   * In the meantime, this prop is only used to know where to display the checkbox
   * in the case where the Card is selectable
   */
  layout?: 'horizontal' | 'vertical'
  hideHeaderBorder?: boolean
}

/**
 * This class is a wrap around [antd card](https://ant.design/components/card/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 */
export const InternalCard = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      loading,
      loadingContent,
      children,
      selectable,
      isSelected: controlledIsSelected,
      initialIsSelected,
      onClick,
      title,
      extra,
      checkbox,
      cover,
      layout = 'vertical',
      hideHeaderBorder,
      ...props
    }: CardProps,
    ref
  ) => {
    const [internalIsSelected, setInternalIsSelected] = useState(initialIsSelected ?? false)
    const isSelected = isBoolean(controlledIsSelected) ? controlledIsSelected : internalIsSelected

    const handleOnClick: CardProps['onClick'] = (e) => {
      const newIsSelected = !isSelected
      setInternalIsSelected(newIsSelected)
      if (onClick) {
        onClick(e)
      }
    }

    // Allow users to press enter to simulate a click event
    const handleKeyDown = onClick ? createKeydownFromClick(onClick, { keys: ['Enter'] }) : undefined

    const renderCheckbox = () =>
      checkbox ? (
        <div className={cx('u-margin-left--small-3', { 'app-card-checkbox--cover': cover })}>{checkbox}</div>
      ) : (
        <Checkbox
          className={cx('u-margin-left--small-3', 'app-card-checkbox', { 'app-card-checkbox--cover': cover })}
          checked={isSelected}
          value={isSelected}
          aria-checked={isSelected}
        />
      )

    const classNames = cx(
      cardClsPrefix,
      {
        [`${cardClsPrefix}--hide-header-border`]: hideHeaderBorder,
        [`${cardClsPrefix}--selectable`]: selectable,
        [`${cardClsPrefix}--selected`]: isSelected,
        [`${cardClsPrefix}--flat`]: layout === 'horizontal'
      },
      className
    )

    return (
      <AntdCard
        ref={ref}
        data-testid="app-card"
        loading={!loadingContent && loading}
        onClick={handleOnClick}
        onKeyDown={handleKeyDown}
        title={title}
        extra={
          // If there is a title or extra (showed top right), we want
          // to show the checkbox for selectable card AFTER the 'extra'
          // or at the 'extra' position if no 'extra' prop
          extra || title ? (
            <span style={{ position: 'relative' }}>
              {extra}
              {selectable && (!cover || layout === 'horizontal') ? renderCheckbox() : null}
            </span>
          ) : null
        }
        cover={
          cover ? (
            <div className="relative">
              {cover}
              {selectable ? layout === 'vertical' && renderCheckbox() : null}
            </div>
          ) : null
        }
        {...props}
        className={classNames}
      >
        {loading && loadingContent ? loadingContent : null}
        {!loading ? (
          // If there is only a Card body we show the checkbox for selectable
          // cards after the body content
          !title && !extra && (!cover || layout === 'horizontal') && selectable ? (
            <div className="u-flex-1 u-flex-justify-space-between max-w-full" style={{ position: 'relative' }}>
              <div className="u-flex-1 max-w-full">{children}</div>
              {renderCheckbox()}
            </div>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment -- Don't remember why I did this but it's not useless
            <>{children}</>
          )
        ) : null}
      </AntdCard>
    )
  }
)

InternalCard.displayName = 'Card'
const Classes = {
  // Replaces the header horizontal padding with margins so the border will also have some spacing on the sides
  HeaderBorderHorizontalMargin: `${cardClsPrefix}__header-border-margin`
}

type CardType = typeof InternalCard
interface CardInterface extends CardType {
  Grid: typeof Grid
  Meta: typeof Meta
  Classes: typeof Classes
}
export const Card = InternalCard as CardInterface

Card.Grid = Grid
Card.Meta = Meta
Card.Classes = Classes
